import { Alert } from "@/components/ui/Alert";
import { Skeleton } from "@/components/ui/Skeleton";
import { formatDollarsRound } from "@/customer/catalog/pricing";
import type { CartQuoteResponse, PriceLine } from "@/customer/cart/types";

// Cart-quote breakdown rendered inside the checkout sidebar. Distinct
// from `<PriceBreakdown>` over in customer/pricing/ because the cart
// quote carries `tax_cents` + `surcharge_cents` that aren't part of
// the public type-detail quote shape — and the footer copy here must
// not say "computed at checkout" (we're already at checkout).
interface Props {
  quote: CartQuoteResponse | undefined;
  isPending?: boolean;
}

export function CheckoutPriceBreakdown({ quote, isPending }: Props): JSX.Element {
  if (isPending) return <SidebarSkeleton />;
  if (!quote) return <SidebarSkeleton />;

  if (!quote.ok) {
    return (
      <Alert variant="destructive">
        <p className="text-sm font-medium">We couldn&rsquo;t price this cart.</p>
        {flattenErrors(quote.errors).length > 0 ? (
          <ul className="mt-2 list-disc space-y-0.5 pl-5 text-xs">
            {flattenErrors(quote.errors).map((msg, i) => (
              <li key={i}>{msg}</li>
            ))}
          </ul>
        ) : (
          <p className="mt-2 text-xs">Fill in the remaining steps and we&rsquo;ll quote it.</p>
        )}
      </Alert>
    );
  }

  return (
    <div className="space-y-3">
      {quote.lines && quote.lines.length > 0 ? (
        <ul className="divide-y divide-zinc-100">
          {quote.lines.map((line, i) => (
            <LineRow key={`${line.section}-${i}`} line={line} />
          ))}
        </ul>
      ) : (
        <p className="text-sm text-zinc-500">Add a delivery address and dates for a full quote.</p>
      )}

      {quote.subtotal_cents > 0 ? (
        <div className="space-y-1 border-t border-zinc-100 pt-3 text-sm">
          <SummaryRow label="Subtotal" cents={quote.subtotal_cents} />
          {quote.tax_cents > 0 ? <SummaryRow label="Tax" cents={quote.tax_cents} /> : null}
          {quote.surcharge_cents > 0 ? (
            <SummaryRow label="Card processing surcharge" cents={quote.surcharge_cents} />
          ) : null}
        </div>
      ) : null}

      <div
        data-testid="checkout-summary-total"
        className="flex items-baseline justify-between border-t-2 border-zinc-200 pt-3"
      >
        <span className="text-sm font-semibold uppercase tracking-wide text-brand-black">Total</span>
        <span className="text-2xl font-semibold text-brand-black">
          {formatDollarsRound(quote.total_cents)}
        </span>
      </div>
    </div>
  );
}

function LineRow({ line }: { line: PriceLine }): JSX.Element {
  return (
    <li className="flex items-baseline justify-between gap-3 py-2 text-sm">
      <div className="min-w-0">
        <p className="truncate font-medium text-brand-black">{line.label}</p>
        {line.detail ? <p className="truncate text-xs text-zinc-500">{line.detail}</p> : null}
      </div>
      <span className="shrink-0 tabular-nums text-brand-black">
        {formatDollarsRound(line.amount_cents)}
      </span>
    </li>
  );
}

function SummaryRow({ label, cents }: { label: string; cents: number }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between text-zinc-600">
      <span>{label}</span>
      <span className="tabular-nums">{formatDollarsRound(cents)}</span>
    </div>
  );
}

function SidebarSkeleton(): JSX.Element {
  return (
    <div className="space-y-3" aria-busy="true">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-4 w-1/2" />
      <Skeleton className="h-8 w-full" />
    </div>
  );
}

function flattenErrors(errors: Record<string, unknown>): string[] {
  const out: string[] = [];
  for (const value of Object.values(errors)) {
    if (Array.isArray(value)) {
      for (const v of value) if (typeof v === "string") out.push(v);
    } else if (typeof value === "string") {
      out.push(value);
    }
  }
  return out;
}
